import { useEffect, useRef, useState, useCallback } from 'react';
import { WS_BASE_URL, WS_LOBBY_URL, WS_GIZMOS_BASE_URL, WS_GIZMOS_LOBBY_URL } from '../api/config';

const AUTH_CLOSE_CODES = new Set([4001, 4003]);

function useAuthenticatedWebSocket(createUrl, enabled, onMessage, options = {}) {
  const [connected, setConnected] = useState(false);
  const [closeCode, setCloseCode] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const shouldReconnectRef = useRef(true);
  const onMessageRef = useRef(onMessage);
  const optionsRef = useRef(options);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { optionsRef.current = options; }, [options]);

  const connect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token || !enabled) return;

    const url = createUrl(token);
    if (!url) return;

    shouldReconnectRef.current = true;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setCloseCode(null);
      clearTimeout(reconnectTimer.current);
      optionsRef.current.onOpen?.();
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        onMessageRef.current?.(data);
      } catch {}
    };

    ws.onclose = (event) => {
      setConnected(false);
      setCloseCode(event.code);
      optionsRef.current.onClose?.(event);

      if (AUTH_CLOSE_CODES.has(event.code)) {
        shouldReconnectRef.current = false;
        return;
      }

      if (shouldReconnectRef.current) {
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => ws.close();
  }, [createUrl, enabled]);

  useEffect(() => {
    connect();
    return () => {
      shouldReconnectRef.current = false;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { connected, closeCode };
}

export function useGameWebSocket(gameId, onMessage, options) {
  const createUrl = useCallback(
    (token) => gameId ? `${WS_BASE_URL}/${gameId}/?token=${token}` : null,
    [gameId]
  );
  return useAuthenticatedWebSocket(createUrl, Boolean(gameId), onMessage, options);
}

export function useLobbyWebSocket(onMessage, options) {
  const createUrl = useCallback(
    (token) => `${WS_LOBBY_URL}?token=${token}`,
    []
  );
  return useAuthenticatedWebSocket(createUrl, true, onMessage, options);
}

export function useGizmosGameWebSocket(gameId, onMessage, options) {
  const createUrl = useCallback(
    (token) => gameId ? `${WS_GIZMOS_BASE_URL}/${gameId}/?token=${token}` : null,
    [gameId]
  );
  return useAuthenticatedWebSocket(createUrl, Boolean(gameId), onMessage, options);
}

export function useGizmosLobbyWebSocket(onMessage, options) {
  const createUrl = useCallback(
    (token) => `${WS_GIZMOS_LOBBY_URL}?token=${token}`,
    []
  );
  return useAuthenticatedWebSocket(createUrl, true, onMessage, options);
}
